/**
 * Passkeys for the Journal's clasp (journal redesign 2026-10-27): Face ID /
 * Touch ID through the platform authenticator, via navigator.credentials. The
 * server makes the challenge and checks the answer; this only moves the bytes
 * between the browser's ArrayBuffers and the JSON the API speaks (base64url).
 */

export function bufToB64url (buf) {
  const b = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function b64urlToBuf (s) {
  const b64 = String(s || '').replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const bin = atob(pad)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

const unwrap = (o) => (o && o.publicKey ? o.publicKey : o) || {}
const ids = (list) => (list || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) }))

export function toCreationOptions (json) {
  const o = unwrap(json)
  return { ...o, challenge: b64urlToBuf(o.challenge), user: { ...o.user, id: b64urlToBuf(o.user && o.user.id) }, excludeCredentials: ids(o.excludeCredentials) }
}

export function toRequestOptions (json) {
  const o = unwrap(json)
  return { ...o, challenge: b64urlToBuf(o.challenge), allowCredentials: ids(o.allowCredentials) }
}

const enc = (v) => (v instanceof ArrayBuffer ? bufToB64url(v) : (v && v.buffer instanceof ArrayBuffer ? bufToB64url(v.buffer) : (v == null ? null : v)))

export function credentialToJSON (cred) {
  const r = cred.response || {}
  const response = {}
  for (const k of ['clientDataJSON', 'attestationObject', 'authenticatorData', 'signature', 'userHandle']) {
    if (k in r) response[k] = enc(r[k])
  }
  if (typeof r.getTransports === 'function') response.transports = r.getTransports()
  return { id: cred.id, rawId: enc(cred.rawId), type: cred.type, response }
}

/** Is there a platform authenticator (Face ID, Touch ID, Windows Hello) here? */
export async function passkeyAvailable () {
  try {
    const P = typeof window !== 'undefined' ? window.PublicKeyCredential : undefined
    if (!P || typeof P.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') return false
    return !!(await P.isUserVerifyingPlatformAuthenticatorAvailable())
  } catch (e) { return false }
}

/** What the "Open with …" button calls the platform's passkey. */
export function bioName (ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  if (/iPhone|iPad|iPod/.test(ua)) return 'Face ID'
  if (/Macintosh|Mac OS X/.test(ua)) return 'Touch ID'
  if (/Windows/.test(ua)) return 'Windows Hello'
  if (/Android/.test(ua)) return 'your fingerprint'
  return 'your passkey'
}

export async function createPasskey (optionsJSON) {
  const cred = await navigator.credentials.create({ publicKey: toCreationOptions(optionsJSON) })
  return credentialToJSON(cred)
}

export async function getPasskey (optionsJSON) {
  const cred = await navigator.credentials.get({ publicKey: toRequestOptions(optionsJSON) })
  return credentialToJSON(cred)
}
